/**
 * @module line/linecommand
 */

import { Command } from 'ckeditor5/src/core';
import { first } from 'ckeditor5/src/utils';

/**
 * The generic line command plugin.
 *
 * @extends Command
 */
export default class LineCommand extends Command {

	/**
	 * Define what attribute key the command should use and what is the default value in case none is defined.
	 *
	 * @param {Editor} editor
	 * @param {String} attributeKey
	 * @param {String} defaultValue
	 */
	constructor( editor, attributeKey, defaultValue ) {
		super( editor );

		/**
		 * @private
		 * @type {String}
		 */
		this.attributeKey = attributeKey;
		/**
		 * @private
		 * @type {String}
		 */
		this.defaultValue = defaultValue;
	}

	/**
	 * @inheritDoc
	 */
	refresh() {
		const firstBlock = first( this.editor.model.document.selection.getSelectedBlocks() );

		// As first check whether to enable or disable the command as the value will always be false if the command cannot be enabled.
		this.isEnabled = !!firstBlock && this._canHaveAttribute( firstBlock );

		/**
		 * A value of the current block's attribute.
		 *
		 * @observable
		 * @readonly
		 * @member {String} #value
		 */
		this.value = ( this.isEnabled && firstBlock.hasAttribute( this.attributeKey ) ) ? firstBlock.getAttribute( this.attributeKey ) : this.defaultValue;
	}

	/**
	 * Executes the command. Applies the attribute `value` to the selected blocks.
	 * If no `value` is passed, the `value` is the default one or it is equal to the currently selected block's attribute,
	 * the command will remove the attribute from the selected blocks.
	 *
	 * @param {Object} [options] Options for the executed command.
	 * @param {String} [options.value] The value to apply.
	 * @fires execute
	 */
	execute( options = {} ) {
		const editor = this.editor;
		const model = editor.model;
		const doc = model.document;

		const value = options.value;

		model.change( writer => {
			const blocks = Array.from( doc.selection.getSelectedBlocks() ).filter( block => this._canHaveAttribute( block ) );
			const currentValue = blocks[ 0 ].getAttribute( this.attributeKey );

			const removeValue = value === this.defaultValue || currentValue === value || !value;

			if ( removeValue ) {
				removeValueFromSelection( blocks, writer, this.attributeKey );
			} else {
				setValueOnSelection( blocks, writer, this.attributeKey, value );
			}
		} );
	}

	/**
	 * Checks whether a block can have the attribute set.
	 *
	 * @private
	 * @param {Element} block The block to be checked.
	 * @returns {Boolean}
	 */
	_canHaveAttribute( block ) {
		return this.editor.model.schema.checkAttribute( block, this.attributeKey );
	}
}

// Removes the attribute from blocks.
// @private
function removeValueFromSelection( blocks, writer, attributeKey ) {
	for ( const block of blocks ) {
		writer.removeAttribute( attributeKey, block );
	}
}

// Sets the attribute on blocks.
// @private
function setValueOnSelection( blocks, writer, attributeKey, value ) {
	for ( const block of blocks ) {
		writer.setAttribute( attributeKey, value, block );
	}
}
